import React, { useEffect, useState } from 'react';
import { RAZORPAY_CONFIG } from '../config/razorpay';
import settingsService from '../services/settingsService';
import './RazorpayPayment.css';
import api from '../config/api';

const RazorpayPayment = ({ 
  amount, 
  currency = 'INR', 
  onSuccess, 
  onError, 
  userDetails,
  bookingDetails,
  bookingId,
  onCreatePendingBooking,
  isCreatingPendingBooking,
  showSummary = true,
  showTerms = true,
  termsAccepted: externalTermsAccepted = null
}) => {
  // Use external termsAccepted if provided, otherwise use internal state
  const [internalTermsAccepted, setInternalTermsAccepted] = useState(false);
  const termsAccepted = externalTermsAccepted !== null ? externalTermsAccepted : internalTermsAccepted;
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [razorpayKey, setRazorpayKey] = useState(RAZORPAY_CONFIG.keyId);
  const [isLoadingKey, setIsLoadingKey] = useState(false);

  const showError = (message) => {
    setErrorMessage(message);
    setShowErrorPopup(true);
  };
  
  useEffect(() => {
    let isMounted = true;
    let scriptElement = null;
    
    // Fetch Razorpay key from admin settings
    const fetchRazorpayKey = async () => {
      if (!isMounted) return;
      
      setIsLoadingKey(true);
      try {
        // Force refresh to get the latest settings
        const paymentSettings = await settingsService.getPaymentSettings(true);
        if (paymentSettings && paymentSettings.razorpayKeyId) {
          // Validate that it's not the placeholder key
          if (paymentSettings.razorpayKeyId === 'rzp_test_1234567890') {
            console.warn('Invalid placeholder key detected, using fallback key');
            setRazorpayKey(RAZORPAY_CONFIG.keyId);
          } else {
            setRazorpayKey(paymentSettings.razorpayKeyId);
            // console.log('Using dynamic Razorpay key:', paymentSettings.razorpayKeyId);
          }
        } else {
          // console.log('No Razorpay key in settings, using fallback key:', RAZORPAY_CONFIG.keyId);
          setRazorpayKey(RAZORPAY_CONFIG.keyId);
        }
      } catch (error) {
        console.error('Failed to fetch Razorpay key from settings:', error);
        // console.log('Using fallback Razorpay key:', RAZORPAY_CONFIG.keyId);
        setRazorpayKey(RAZORPAY_CONFIG.keyId);
      } finally {
        if (isMounted) {
          setIsLoadingKey(false);
        }
      }
    };
    
    fetchRazorpayKey();
    
    // Load Razorpay script
    const loadRazorpayScript = () => {
      // Check if script is already loaded
      if (window.Razorpay) {
        return Promise.resolve(true);
      }
      
      // Check if script tag already exists
      const existingScript = document.querySelector('script[src="https://checkout.razorpay.com/v1/checkout.js"]');
      if (existingScript) {
        return Promise.resolve(true);
      }
      
      return new Promise((resolve) => {
        scriptElement = document.createElement('script');
        scriptElement.src = 'https://checkout.razorpay.com/v1/checkout.js';
        scriptElement.async = true;
        scriptElement.onload = () => {
          if (isMounted) resolve(true);
        };
        scriptElement.onerror = () => {
          if (isMounted) resolve(false);
        };
        
        // Only append if component is still mounted and script doesn't exist
        if (isMounted && !document.querySelector('script[src="https://checkout.razorpay.com/v1/checkout.js"]')) {
          document.body.appendChild(scriptElement);
        } else {
          resolve(true);
        }
      });
    };

    loadRazorpayScript();
    
    // Cleanup: Don't remove the script as it might be used by other components
    // Just mark component as unmounted
    return () => {
      isMounted = false;
    };
  }, []);

  const createRazorpayOrder = async (finalBookingId) => {
    return await api.initiateRazorpayPayment({
      amount: Number(amount), // ✅ send rupees
      currency: 'INR',
      customerName: userDetails?.name,
      customerEmail: userDetails?.email,
      customerPhone: userDetails?.phone,
      bookingId: finalBookingId,
    });
  };

  const openRazorpay = async () => {
    // Check if terms are accepted
    if (!termsAccepted) {
      showError('Please accept the Terms and Conditions to proceed with payment.');
      return;
    }

    // Check if Razorpay key is loaded
    if (isLoadingKey) {
      showError('Payment configuration is loading. Please wait a moment and try again.');
      return;
    }

    // Create pending booking before opening payment gateway (if not already created)
    let finalBookingId = bookingId;
    if (onCreatePendingBooking && !finalBookingId) {
      try {
        finalBookingId = await onCreatePendingBooking();
        if (!finalBookingId) {
          // If creation failed, onCreatePendingBooking would have shown error
          return;
        }
      } catch (error) {
        console.error('Failed to create pending booking:', error);
        // Error already shown by onCreatePendingBooking
        return;
      }
    }

    // Debug: Log full userDetails to see what we have
    // console.log('RazorpayPayment - Full userDetails:', userDetails);
    // console.log('RazorpayPayment - userDetails keys:', userDetails ? Object.keys(userDetails) : 'null');
    
    // Get phone number from userDetails (could be phone, mobile, or contact)
    let phoneNumber = userDetails?.phone || userDetails?.mobile || userDetails?.contact || '';
    
    // console.log('RazorpayPayment - Raw phone number:', phoneNumber);
    
    // Clean and format phone number for Razorpay (should be 10 digits)
    // Remove any spaces, dashes, or country codes
    if (phoneNumber) {
      phoneNumber = phoneNumber.toString().replace(/\s+/g, '').replace(/-/g, '').replace(/\+91/g, '');
      // If it's 11 digits and starts with 0, remove the leading 0
      if (phoneNumber.length === 11 && phoneNumber.startsWith('0')) {
        phoneNumber = phoneNumber.substring(1);
      }
      // Ensure it's exactly 10 digits
      if (phoneNumber.length > 10) {
        // Take last 10 digits
        phoneNumber = phoneNumber.substring(phoneNumber.length - 10);
      } else if (phoneNumber.length < 10) {
        // If less than 10 digits, might be invalid
        console.warn('Phone number format might be invalid:', phoneNumber);
        phoneNumber = ''; // Clear invalid phone number
      }
      // If exactly 10 digits, keep it as is (no action needed)
    }

    // console.log('Razorpay prefill data:', {
    //   name: userDetails?.name || '',
    //   email: userDetails?.email || '',
    //   contact: phoneNumber,
    //   hasPhone: !!phoneNumber,
    //   userDetails: userDetails
    // });

    // If no phone number, show warning
    if (!phoneNumber) {
      console.error('No valid phone number found in userDetails!', userDetails);
      showError('Phone number is missing. Please update your profile with a phone number.');
      return;
    }

    // Ensure amount is properly rounded to avoid floating point precision issues
    // Round to 2 decimal places first, then convert to paise (integer)
    const roundedAmount = Math.round(parseFloat(amount) * 100) / 100;
    const amountInPaise = Math.round(roundedAmount * 100);

    // console.log("BOOKING ID SENT TO RAZORPAY:", finalBookingId);
    
    // console.log('Razorpay amount calculation:', {
    //   originalAmount: amount,
    //   roundedAmount: roundedAmount,
    //   amountInPaise: amountInPaise,
    //   type: typeof amount
    // });

    const order = await createRazorpayOrder(finalBookingId);

    const options = {
      key: razorpayKey, // Use dynamic key from admin settings
      amount: amountInPaise, // Razorpay expects amount in paise (must be integer)
      currency: currency,
      name: RAZORPAY_CONFIG.name,
      description: `${RAZORPAY_CONFIG.description} - ${bookingDetails?.movie?.title || 'Movie Ticket'}`,
      image: '/assets/img/logo.png', // Your logo
      order_id: order.id,// You'll get this from your backend
      // handler: function (response) {
      //   console.log('Payment Success:', response);
      //   // Mark as Razorpay payment
      //   onSuccess({
      //     ...response,
      //     paymentMethod: 'Razorpay',
      //     bookingId: finalBookingId
          
      //   });
      // },
      handler: (response) => {
        // console.log('Payment Success:', response);
                // Mark as Razorpay payment
        
        onSuccess({
        ...response,
        paymentMethod: 'Razorpay',
        bookingId: finalBookingId,
        captured: true, // auto-capture
        });
        },
      prefill: {
        name: userDetails?.name || '',
        email: userDetails?.email || '',
        contact: phoneNumber || userDetails?.phone || userDetails?.mobile || '',
      },
      // Ensure contact is always provided
      read_only: {
        contact: !!phoneNumber, // Make contact read-only if we have a valid phone
      },
      notes: {
        address: 'Cinema Booking',
        // booking_id: finalBookingId || bookingDetails?.bookingId || 'temp_booking_id',
        booking_id: finalBookingId || bookingDetails?.bookingId || null,
        movie: bookingDetails?.movie?.title || '',
        seats: bookingDetails?.seats?.map(seat => seat.id).join(', ') || '',
        showtime: bookingDetails?.showtime?.date + ' ' + bookingDetails?.showtime?.time || '',
      },
      theme: RAZORPAY_CONFIG.theme,
      modal: {
        ondismiss: function() {
          // console.log('Payment modal closed');
        }
      }
    };

    const rzp = new window.Razorpay(options);
    rzp.on('payment.failed', function (response) {
      console.log('Payment Failed:', response.error);
      onError(response.error);
    });
    
    rzp.open();
  };

  return (
    <div className="razorpay-payment-option">
      {showSummary && (
        <div className="payment-summary">
          <h3>Payment Summary</h3>
          <div className="amount-breakdown">
            <div className="amount-item">
              <span>Movie Tickets</span>
              <span>₹{(bookingDetails?.ticketPrice || 0).toFixed(2)}</span>
            </div>
            <div className="amount-item">
              <span>Snacks</span>
              <span>₹{(bookingDetails?.snacksPrice || 0).toFixed(2)}</span>
            </div>
            <div className="amount-item">
              <span>Taxes & Fees</span>
              <span>₹{(bookingDetails?.taxes || 0).toFixed(2)}</span>
            </div>
            <div className="amount-item total">
              <span>Total Amount</span>
              <span>₹{amount.toFixed(2)}</span>
            </div>
          </div>
        </div>
      )}

      <div className="payment-method-card">
        <button 
          className={`payment-method-btn razorpay-btn ${!termsAccepted || isCreatingPendingBooking ? 'disabled' : ''}`}
          onClick={openRazorpay}
          disabled={!termsAccepted || isCreatingPendingBooking}
        >
          <div className="payment-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" fill="#3395FF"/>
            </svg>
          </div>
          <div className="payment-info">
            <span className="payment-name">Pay with Razorpay</span>
            <span className="payment-desc">Cards, UPI, Net Banking, Wallets</span>
          </div>
          <div className="payment-arrow">→</div>
        </button>
        {showTerms && (
          <div className="terms-checkbox-container">
            <label className="terms-checkbox-label">
              <input
                type="checkbox"
                checked={termsAccepted}
                onChange={(e) => {
                  if (externalTermsAccepted === null) {
                    setInternalTermsAccepted(e.target.checked);
                  }
                }}
                disabled={externalTermsAccepted !== null}
                className="terms-checkbox"
              />
              <span className="terms-text">
                I agree to the <a href="/pages/terms-and-conditions" target="_blank" rel="noopener noreferrer" className="terms-link">Terms and Conditions</a>
              </span>
            </label>
          </div>
        )}
      </div>

      {showErrorPopup && (
      <div className="error-popup-overlay" onClick={() => setShowErrorPopup(false)}>
        <div className="error-popup-content" onClick={(e) => e.stopPropagation()}>
          <div className="error-popup-icon">
            <i className="fas fa-exclamation-circle"></i>
          </div>
          <div className="error-popup-message">
            {errorMessage}
          </div>
          <button
            className="error-popup-button"
            onClick={() => setShowErrorPopup(false)}
          >
            OK
          </button>
        </div>
      </div>
    )}
    </div>
  );
};

export default RazorpayPayment;
